/**
 * Input masks for <mellow:input mask="…">.
 *
 * Tokens: 9 digit, A uppercase letter, a lowercase letter, L any letter, H hex digit (uppercase),
 * * letter or digit, \ makes the next character literal. Everything else is a literal that is
 * inserted automatically. Presets: ipv4, ipv6 (smart formatting + validation), mac.
 *
 * Formatting runs in a capturing input listener on document, i.e. before wire:model reads the value.
 */
const TOKENS = {
    9: /\d/u,
    A: /[A-Z]/u,
    a: /[a-z]/u,
    L: /\p{L}/u,
    H: /[0-9A-F]/u,
    '*': /[\p{L}\d]/u,
};

const PRESETS = { mac: 'HH:HH:HH:HH:HH:HH' };

const cache = new Map();

/** Parses a mask into [{ test, literal }] parts. */
function parse(mask, custom = {}) {
    const key = mask + JSON.stringify(custom);
    if (cache.has(key)) return cache.get(key);

    const tokens = { ...TOKENS };
    for (const [char, source] of Object.entries(custom)) tokens[char] = new RegExp(`^(?:${source})$`, 'u');

    const parts = [];
    const chars = [...mask];
    for (let i = 0; i < chars.length; i++) {
        if (chars[i] === '\\' && i + 1 < chars.length) {
            parts.push({ literal: chars[++i] });
        } else if (tokens[chars[i]]) {
            parts.push({ test: tokens[chars[i]] });
        } else {
            parts.push({ literal: chars[i] });
        }
    }

    cache.set(key, parts);
    return parts;
}

/** The character as accepted by the token – as typed, or converted to upper / lower case. */
function accept(test, char) {
    for (const candidate of [char, char.toUpperCase(), char.toLowerCase()]) {
        if (candidate.length === 1 && test.test(candidate)) return candidate;
    }
    return null;
}

/** Formats text with a fixed mask. Characters that don't fit are dropped. */
export function applyMask(value, mask, custom = {}) {
    const parts = parse(mask, custom);
    const chars = [...value];
    let out = '';
    let i = 0;

    for (const part of parts) {
        if (i >= chars.length) break;

        if (part.literal !== undefined) {
            out += part.literal;
            if (chars[i] === part.literal) i++;
            continue;
        }

        let accepted = null;
        while (i < chars.length && accepted === null) accepted = accept(part.test, chars[i++]);
        if (accepted === null) {
            // Nothing left that fits: don't end on literals we just added.
            while (out && parts[[...out].length - 1]?.literal !== undefined) out = [...out].slice(0, -1).join('');
            break;
        }
        out += accepted;
    }

    return out;
}

/** 1921681 → 192.168.1: dots are inserted when an octet is full or would exceed 255. */
function formatIpv4(value) {
    const octets = [''];
    for (const char of value) {
        const current = octets[octets.length - 1];
        if (char === '.' || char === ',') {
            if (current !== '' && octets.length < 4) octets.push('');
        } else if (/\d/.test(char)) {
            if (current.length < 3 && Number(current + char) <= 255 && current !== '0') {
                octets[octets.length - 1] = current + char;
            } else if (octets.length < 4) {
                octets.push(char);
            }
        }
    }
    return octets.join('.');
}

function formatIpv6(value) {
    return value.toLowerCase().replace(/[^0-9a-f:.]/g, '').replace(/:{3,}/g, '::').slice(0, 45);
}

export function isIpv4(value) {
    return /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/.test(value);
}

export function isIpv6(value) {
    if (!/^[0-9a-f:.]+$/i.test(value)) return false;

    // An embedded IPv4 address (::ffff:192.168.0.1) counts as two groups.
    const lastColon = value.lastIndexOf(':');
    const tail = value.slice(lastColon + 1);
    let address = value;
    if (tail.includes('.')) {
        if (lastColon === -1 || !isIpv4(tail)) return false;
        address = `${value.slice(0, lastColon + 1)}0:0`;
    }

    const halves = address.split('::');
    if (halves.length > 2) return false;

    const groups = halves.flatMap((half) => (half === '' ? [] : half.split(':')));
    if (!groups.every((group) => /^[0-9a-f]{1,4}$/i.test(group))) return false;

    return halves.length === 2 ? groups.length <= 7 : groups.length === 8;
}

function config(input) {
    const mask = input.dataset.mellowMask;
    let tokens = {};
    try {
        tokens = JSON.parse(input.dataset.mellowMaskTokens || '{}');
    } catch {
        // Invalid token JSON: use the built-in tokens only.
    }
    return { mask: PRESETS[mask] ?? mask, preset: mask, tokens };
}

export function format(input, value) {
    const { mask, preset, tokens } = config(input);
    if (preset === 'ipv4') return formatIpv4(value);
    if (preset === 'ipv6') return formatIpv6(value);
    return applyMask(value, mask, tokens);
}

/** Error message for an invalid value, '' when valid (or empty – "required" is the browser's job). */
export function problem(input, t) {
    const { mask, preset, tokens } = config(input);
    const value = input.value;
    if (value === '') return '';
    if (preset === 'ipv4') return isIpv4(value) ? '' : t('mask.invalid.ipv4');
    if (preset === 'ipv6') return isIpv6(value) ? '' : t('mask.invalid.ipv6');
    return [...value].length === parse(mask, tokens).length ? '' : t('mask.invalid.incomplete');
}

export function registerMasks(t) {
    // Inputs the user has left once: from then on, the invalid state is shown.
    const touched = new WeakSet();

    const validate = (input) => {
        const message = problem(input, t);
        input.setCustomValidity(message);
        if (!touched.has(input)) return;

        // Server errors (rendered with data-invalid) take precedence and stay untouched.
        const server = input.closest('[data-invalid]');
        if (message) input.setAttribute('aria-invalid', 'true');
        else if (!server) input.removeAttribute('aria-invalid');

        const error = input.id && document.getElementById(`${input.id}-error`);
        if (!error || server) return;

        if (message && error.dataset.mellowMaskError !== message) {
            const icon = input.closest('.mellow-input')?.querySelector('template[data-mellow-mask-icon]');
            const text = document.createElement('span');
            text.textContent = message;
            error.replaceChildren(...(icon ? [icon.content.cloneNode(true)] : []), text);
            error.removeAttribute('data-empty');
            error.dataset.mellowMaskError = message;
        } else if (!message && error.dataset.mellowMaskError) {
            error.replaceChildren();
            error.setAttribute('data-empty', '');
            delete error.dataset.mellowMaskError;
        }
    };

    document.addEventListener('input', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-mellow-mask') || event.isComposing) return;

        // Deleting in the middle would shift the following characters into slots they don't fit
        // (and drop them). Leave deletions as they are – the next insertion formats the whole value.
        if (event.inputType?.startsWith('delete')) {
            validate(input);
            return;
        }

        const value = input.value;
        const caret = input.selectionStart ?? value.length;
        const formatted = format(input, value);

        if (formatted !== value) {
            const atEnd = caret >= value.length;
            input.value = formatted;
            const position = atEnd ? formatted.length : format(input, value.slice(0, caret)).length;
            if (document.activeElement === input) input.setSelectionRange(position, position);
        }

        validate(input);
    }, true);

    document.addEventListener('focusout', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-mellow-mask') || input.value === '') return;
        touched.add(input);
        validate(input);
    });

    // Initial values and values set by Livewire: keep the validity in sync (morphs drop aria-invalid).
    return (root = document) => root.querySelectorAll?.('input[data-mellow-mask]').forEach(validate);
}
